import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo } from 'react';

import { Empty } from '@/components/UI/Empty';

import Footer from '@/renderer/Footer';
import MarketingHeader from '@/pages/(marketing)/index/components/Header';
import { useDebounce } from '@/renderer/utils/hooks/useDebounce';
import { usePageContext } from '@/renderer/utils/usePageContext';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

import { mixpanelTrack } from '@/renderer/utils/stat/mixp';
import { BettingTalkIcon, CBBIcon, CFBIcon, CFBIconSolid, NFLIcon, NFLIconSolid, RASPlusIcon } from './components/ProductIcons';
import ProductList from './components/ProductList';
import ProductMenu from './components/ProductMenu';
import ProductPage from './components/ProductPage';
import { sportIdFromName } from './components/productHelpers';

export const getProducts: RequestSchema<
  API.MembershipProduct<true>[], // actually API.WithPage but useRequest only returns the `data` field
  API.PageInput & API.WithData<{ ids?: number[] }>
> = {
  input: '/api/v1/product',
};

const OrderPage: React.FC<{ tag?: string }> = ({ tag: givenTag }) => {
  const pageContext = usePageContext();
  const tag = pageContext.routeParams.tag || givenTag;

  const opts = useMemo(
    () => ({
      skipRefresh: true,
      query: {
        data: {
          id: pageContext.routeParams.sportId ? undefined : pageContext.routeParams.productId,
          sportId: sportIdFromName(pageContext.routeParams.sportId),
          'tags$@>': tag && [tag],
        },
        page: {
          limit: 100,
        },
      },
    }),
    [pageContext.routeParams.productId, pageContext.routeParams.sportId, tag],
  );
  const productsRequest = useRequest(getProducts, opts);
  const isLoading = useDebounce(productsRequest.loading, 300);

  const products = productsRequest.data;
  const error = productsRequest.error;

  const product = useMemo(() => {
    if (products) {
      if (pageContext.routeParams.productId) {
        return (
          products.find((prod) => `${prod.id}` === `${pageContext.routeParams.productId}`) ||
          undefined
        );
      }

      if (pageContext.routeParams.sportId) {
        let best: API.MembershipProduct<true> | undefined = undefined;
        const sportId = sportIdFromName(pageContext.routeParams.sportId);

        if (typeof sportId === 'number') {
          for (const prod of products) {
            if (prod.sportIds?.includes(sportId)) {
              if (!best) {
                best = prod;
              }

              if (prod.sportIds.length === 1) {
                best = prod;
                break;
              }
            }
          }

          return best || undefined;
        }
      }

      if (tag) {
        let best: API.MembershipProduct<true> | undefined = undefined;

        for (const prod of products) {
          if (prod.tags?.includes(tag)) {
            if (!best) {
              best = prod;
            }

            if (prod.tags.length === 1) {
              best = prod;
              break;
            }
          }
        }

        return best || undefined;
      }
    }

    return undefined;
  }, [products, pageContext.routeParams.productId, pageContext.routeParams.sportId, tag]);

  const routeParams = pageContext.routeParams;
  const isAllProductsPage = !routeParams?.tag && !routeParams?.sportId && !routeParams?.productId;
  const singleProduct = isAllProductsPage && products?.length === 1 ? products[0] : undefined;

  const activeProduct = product || singleProduct;

  const IconComponent = useMemo(() => {
    switch (sportIdFromName(pageContext.routeParams.sportId) || activeProduct?.sportIds?.[0]) {
      case 1:
        return CFBIconSolid;

      case 2:
        return CBBIcon;

      case 3:
        return NFLIconSolid;
    }

    if (activeProduct?.tags?.includes('ras-plus')) {
      return RASPlusIcon;
    }

    if (activeProduct?.tags?.includes('betting-talk')) {
      return BettingTalkIcon;
    }

    // @todo better default icon?
    return CFBIconSolid;
  }, [pageContext.routeParams.sportId, activeProduct?.sportIds, activeProduct?.tags]);

  useEffect(() => {
    if (product?.id) {
      mixpanelTrack('order/client:view', { product_id: product?.id });
    }
  }, [product]);

  return (
    <div className="overflow-x-hidden bg-zinc-50 dark:bg-zinc-900">
      <MarketingHeader absolute={false} />
      <ProductMenu
        products={products}
        product={product}
        isLoading={isLoading}
        routeParams={pageContext.routeParams}
      />
      <div>
        <div className="mx-auto max-w-5xl px-4 py-6 2xl:px-0">
          {pageContext.urlParsed?.search?.source && (
            <div className="mb-8 flex flex-row items-center justify-start space-x-2 rounded-sm bg-amber-50 p-4 text-amber-700">
              <LockClosedIcon height={24} className="size-6 shrink-0" />
              <span className="font-medium text-sm sm:text-base">
                You must be a Betting Talk member to view that thread. Join below.
              </span>
            </div>
          )}
          {/* Product */}
          {!isLoading &&
            !products?.length &&
            (error ? (
              <Empty
                IconComponent={IconComponent}
                title="We ran into an error fetching our products. Please try again later."
                description={`Error message: ${error.message}.`}
              />
            ) : (
              <Empty
                IconComponent={IconComponent}
                title="No Products Found"
                description="We're sorry, we aren't currently offering any products that match your search."
              />
            ))}

          {((!products && isLoading) || (products && products?.length > 0)) &&
            (isAllProductsPage && !singleProduct ? (
              <ProductList
                products={products}
                isLoading={isLoading}
                IconComponent={IconComponent}
              />
            ) : (
              <>
                {(product || singleProduct) && (
                  <ProductPage
                    product={(product || singleProduct)!}
                    IconComponent={IconComponent}
                    isLoading={isLoading}
                  />
                )}
              </>
            ))}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OrderPage;
